import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown, Info, X } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { EditableField, type FieldStatus } from "./editable-field";
import type { FieldSource } from "./field-sources";
import { useEditDealField } from "./use-edit-deal-field";
import { useStageDraftInBulk } from "./bulk-edit-context";

type SelectOption = { value: string; label: string };

/**
 * Renders a long string clamped to N lines with a "View more" / "View less"
 * toggle that only appears if the content actually overflows the clamp.
 */
function ClampedText({ text, lines }: { text: string; lines: number }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowed, setOverflowed] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The element is clamped while measuring — compare full scrollHeight to
    // the rendered clientHeight to detect overflow.
    setOverflowed(el.scrollHeight - el.clientHeight > 1);
  }, [text, lines]);

  const lineClampStyle: React.CSSProperties = expanded
    ? {}
    : {
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      };

  return (
    <div className="space-y-1">
      <p
        ref={ref}
        className="whitespace-pre-wrap leading-relaxed"
        style={lineClampStyle}
      >
        {text}
      </p>
      {overflowed && (
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {expanded ? "View less" : "View more"}
        </button>
      )}
    </div>
  );
}

// ── Text field ─────────────────────────────────────────────────────────────────

export interface FieldSuggestion {
  url: string;
  title?: string;
  description?: string;
  favicon?: string;
}

export function EditableTextField({
  label,
  fieldName,
  value,
  readValue,
  placeholder,
  type = "text",
  status,
  suggestions,
  sources,
}: {
  label: string;
  fieldName: string;
  value: string | null;
  readValue?: ReactNode;
  placeholder?: string;
  type?: "text" | "url";
  status?: FieldStatus;
  suggestions?: FieldSuggestion[];
  sources?: FieldSource[];
}) {
  const { save } = useEditDealField();
  const [draft, setDraft] = useState(value ?? "");
  useStageDraftInBulk(fieldName, draft);

  return (
    <EditableField
      label={label}
      status={status}
      sources={sources}
      readValue={readValue ?? value}
      editor={
        <div className="flex flex-col gap-2">
          <input
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded border border-border bg-input px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
          />
          {suggestions && suggestions.length > 0 && (
            <div className="rounded-md border border-border bg-card shadow-sm">
              <div className="border-b border-border px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                AI suggestions
              </div>
              <ul className="max-h-48 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <li key={`${s.url}-${i}`}>
                    <button
                      type="button"
                      onClick={() => setDraft(s.url)}
                      className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none"
                    >
                      {s.favicon && (
                        <img
                          src={s.favicon}
                          alt=""
                          className="mt-0.5 h-4 w-4 shrink-0"
                          onError={(e) => {
                            (
                              e.currentTarget as HTMLImageElement
                            ).style.display = "none";
                          }}
                        />
                      )}
                      <span className="min-w-0 flex-1">
                        {s.title && (
                          <span className="block truncate text-sm font-medium">
                            {s.title}
                          </span>
                        )}
                        <span className="block truncate text-xs text-muted-foreground">
                          {s.url}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      }
      onEnterEdit={() => setDraft(value ?? "")}
      onApprove={value ? () => save(fieldName, value) : undefined}
    />
  );
}

// ── Textarea field ─────────────────────────────────────────────────────────────

export function EditableTextareaField({
  label,
  fieldName,
  value,
  status,
  clampLines,
  sources,
}: {
  label: string;
  fieldName: string;
  value: string | null;
  status?: FieldStatus;
  /** If set, read-mode clamps to N lines with a "View more" / "View less" toggle. */
  clampLines?: number;
  sources?: FieldSource[];
}) {
  const { save } = useEditDealField();
  const [draft, setDraft] = useState(value ?? "");
  useStageDraftInBulk(fieldName, draft);

  return (
    <EditableField
      label={label}
      status={status}
      sources={sources}
      multiline
      readValue={
        value ? (
          clampLines ? (
            <ClampedText text={value} lines={clampLines} />
          ) : (
            <p className="whitespace-pre-wrap leading-relaxed">{value}</p>
          )
        ) : null
      }
      editor={
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          className="w-full resize-none rounded border border-border bg-input px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
        />
      }
      onEnterEdit={() => setDraft(value ?? "")}
      onApprove={value ? () => save(fieldName, value) : undefined}
    />
  );
}

// ── Select field (single) ──────────────────────────────────────────────────────

export function EditableSelectField({
  label,
  fieldName,
  value,
  options,
  readValue,
  status,
  sources,
}: {
  label: string;
  fieldName: string;
  /** Stored value (may be numeric as a string for stage, e.g. "1.1"). */
  value: string | null;
  options: SelectOption[];
  readValue?: ReactNode;
  status?: FieldStatus;
  sources?: FieldSource[];
}) {
  const { save } = useEditDealField();
  const [draft, setDraft] = useState<string>(value ?? "");
  useStageDraftInBulk(fieldName, draft);

  return (
    <EditableField
      label={label}
      status={status}
      sources={sources}
      readValue={
        readValue ??
        (value
          ? options.find((o) => o.value === value)?.label || value
          : null)
      }
      editor={
        <Select value={draft} onValueChange={setDraft}>
          <SelectTrigger className="h-8">
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
      onEnterEdit={() => setDraft(value ?? "")}
      onApprove={value ? () => save(fieldName, value) : undefined}
    />
  );
}

// ── Combobox field (virtualized, for long lists like countries) ────────────────

export function EditableComboboxField({
  label,
  fieldName,
  value,
  options,
  status,
  sources,
}: {
  label: string;
  fieldName: string;
  value: string | null;
  options: SelectOption[];
  status?: FieldStatus;
  sources?: FieldSource[];
}) {
  const { save } = useEditDealField();
  const [draft, setDraft] = useState<string>(value ?? "");
  useStageDraftInBulk(fieldName, draft);

  return (
    <EditableField
      label={label}
      status={status}
      sources={sources}
      readValue={
        value ? options.find((o) => o.value === value)?.label || value : null
      }
      editor={<CommandCombobox options={options} value={draft} onChange={setDraft} />}
      onEnterEdit={() => setDraft(value ?? "")}
      onApprove={value ? () => save(fieldName, value) : undefined}
    />
  );
}

/**
 * Popover + Command single-select combobox: built-in fuzzy search via cmdk,
 * keyboard nav, and a checkmark on the selected row. Lighter weight than
 * VirtualizedCombobox for <~500 options.
 */
function CommandCombobox({
  options,
  value,
  onChange,
}: {
  options: SelectOption[];
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="h-8 w-full justify-between font-normal"
        >
          <span className={cn(!selected && "text-muted-foreground")}>
            {selected?.label ?? "Select…"}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>No match found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.value}`}
                  onSelect={() => {
                    onChange(option.value === value ? "" : option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── Multi-select badge field (business model, revenue model) ───────────────────

export function EditableMultiBadgeField({
  label,
  fieldName,
  value,
  options,
  badgeClassName,
  status,
  renderItemTooltip,
  sources,
}: {
  label: string;
  fieldName: string;
  value: string[];
  options: SelectOption[];
  badgeClassName?: string;
  status?: FieldStatus;
  renderItemTooltip?: (option: SelectOption) => ReactNode;
  sources?: FieldSource[];
}) {
  const { save } = useEditDealField();
  const [draft, setDraft] = useState<string[]>(value);
  useStageDraftInBulk(fieldName, draft);

  const labelsFor = (vals: string[]) =>
    vals.map((v) => options.find((o) => o.value === v)?.label || v);

  // Stored values can collide on label (the AI sometimes pushes both a
  // duplicate raw value and a synonym that resolves to the same display
  // label), so dedupe by label before rendering — otherwise React warns
  // about non-unique keys and the user sees the same badge twice.
  const uniqueLabels = (vals: string[]) => {
    const seen = new Set<string>();
    return labelsFor(vals).filter((l) => {
      if (seen.has(l)) return false;
      seen.add(l);
      return true;
    });
  };

  return (
    <EditableField
      label={label}
      status={status}
      sources={sources}
      readValue={
        value.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {uniqueLabels(value).map((l) => (
              <span
                key={l}
                className={cn(
                  "rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground",
                  badgeClassName
                )}
              >
                {l}
              </span>
            ))}
          </div>
        ) : null
      }
      editor={
        <MultiSelectCombobox
          options={options}
          selected={draft}
          onChange={setDraft}
          placeholder={`Select ${label.toLowerCase()}`}
          renderItemTooltip={renderItemTooltip}
        />
      }
      onEnterEdit={() => setDraft(value)}
      onApprove={value.length > 0 ? () => save(fieldName, value) : undefined}
    />
  );
}

/**
 * Popover + Command multi-select combobox: chips above for selected values
 * (with remove buttons), dropdown below with fuzzy search and checkmark per
 * row.
 */
export function MultiSelectCombobox({
  options,
  selected,
  onChange,
  placeholder = "Select…",
  renderItemTooltip,
}: {
  options: SelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** If provided, each row gets an Info icon with this tooltip content. */
  renderItemTooltip?: (option: SelectOption) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const selectedOptions = selected
    .map((v) => options.find((o) => o.value === v))
    .filter((o): o is SelectOption => !!o);
  const toggle = (v: string) => {
    onChange(
      selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]
    );
  };
  return (
    <div className="flex flex-col gap-2">
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((opt) => (
            <span
              key={opt.value}
              className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium"
            >
              {opt.label}
              <button
                type="button"
                onClick={() => toggle(opt.value)}
                className="rounded-sm hover:bg-foreground/10"
                title="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-8 w-full justify-between font-normal"
          >
            <span
              className={cn(
                selectedOptions.length === 0 && "text-muted-foreground"
              )}
            >
              {selectedOptions.length > 0
                ? `${selectedOptions.length} selected`
                : placeholder}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search…" />
            <CommandList>
              <CommandEmpty>No match found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const tip = renderItemTooltip?.(option);
                  return (
                    <CommandItem
                      key={option.value}
                      value={`${option.label} ${option.value}`}
                      onSelect={() => toggle(option.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          selected.includes(option.value)
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                      <span className="flex-1 truncate">{option.label}</span>
                      {tip && (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                onPointerDown={(e) => e.stopPropagation()}
                                className="ml-2 shrink-0 rounded p-0.5 hover:bg-foreground/10"
                                aria-label={`More info about ${option.label}`}
                              >
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="left"
                              align="center"
                              className="max-w-xs text-xs"
                            >
                              {tip}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
